"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowRight, Loader } from "lucide-react";
import { cn } from "@/lib/utils";
export default function InquiryDetails() {
  return <section id="inquiry-details" className="dark bg-background text-foreground py-24 lg:py-32 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-border to-transparent opacity-20" />
      <div className="absolute -left-64 top-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -right-64 bottom-0 w-96 h-96 bg-secondary/5 rounded-full blur-3xl pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-16 space-y-4">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif font-light tracking-tight text-foreground">
              Inquiry Details
            </h2>
            <p className="text-muted-foreground text-lg font-light max-w-xl mx-auto">
              Please provide the details of your inquiry below. Our team will review your information and respond securely.
            </p>
          </div>

          <div className="bg-card/5 backdrop-blur-sm border border-white/5 p-8 md:p-12 rounded-xl shadow-2xl">
            <FormProvider formId="inquiry_details_secure_your_legacy" sectionName="inquiry-details" defaultValues={{
            full_name: "",
            email_address: "",
            phone_number: "",
            inquiry_type: "",
            assets_range: "",
            message: ""
          }} validationRules={{
            full_name: {
              required: true,
              minLength: 2
            },
            email_address: {
              required: true,
              pattern: "email"
            },
            phone_number: {
              required: true,
              minLength: 10
            },
            inquiry_type: {
              required: true
            },
            message: {
              required: true,
              minLength: 10
            }
          }} submitText="Submit Request" successMessage="Thank you. Your inquiry has been received securely.">
              <InquiryForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}
function InquiryForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-6 animate-in fade-in duration-500">
        <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center border border-primary/50">
          <ArrowRight className="w-8 h-8 text-primary" />
        </div>
        <h3 className="text-2xl font-serif text-foreground">Request Received</h3>
        <p className="text-muted-foreground max-w-md">{successMessage}</p>
        <Button variant="outline" className="mt-4 border-primary/30 hover:bg-primary/10 hover:text-primary" onClick={() => window.location.reload()}>
          Submit Another Inquiry
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {}
        <FormField control={control} name="full_name" render={({
        field
      }) => <FormItem className="relative space-y-0 md:col-span-2">
              <FormControl>
                <Input {...field} placeholder=" " className="peer h-14 px-4 pt-5 pb-2 bg-background/50 border-border/50 focus-visible:border-primary focus-visible:ring-0 rounded-sm transition-all placeholder:text-transparent" />
              </FormControl>
              <FormLabel className="absolute left-4 top-4 text-muted-foreground/70 text-sm transition-all duration-200 peer-focus:-top-2.5 peer-focus:left-2 peer-focus:text-xs peer-focus:text-primary peer-[:not(:placeholder-shown)]:-top-2.5 peer-[:not(:placeholder-shown)]:left-2 peer-[:not(:placeholder-shown)]:text-xs pointer-events-none bg-background/0 px-1">
                Full Name
              </FormLabel>
              <FormMessage className="mt-1 text-xs" />
            </FormItem>} />

        {}
        <FormField control={control} name="email_address" render={({
        field
      }) => <FormItem className="relative space-y-0">
              <FormControl>
                <Input {...field} type="email" placeholder=" " className="peer h-14 px-4 pt-5 pb-2 bg-background/50 border-border/50 focus-visible:border-primary focus-visible:ring-0 rounded-sm transition-all placeholder:text-transparent" />
              </FormControl>
              <FormLabel className="absolute left-4 top-4 text-muted-foreground/70 text-sm transition-all duration-200 peer-focus:-top-2.5 peer-focus:left-2 peer-focus:text-xs peer-focus:text-primary peer-[:not(:placeholder-shown)]:-top-2.5 peer-[:not(:placeholder-shown)]:left-2 peer-[:not(:placeholder-shown)]:text-xs pointer-events-none bg-background/0 px-1">
                Email Address
              </FormLabel>
              <FormMessage className="mt-1 text-xs" />
            </FormItem>} />

        {}
        <FormField control={control} name="phone_number" render={({
        field
      }) => <FormItem className="relative space-y-0">
              <FormControl>
                <Input {...field} type="tel" placeholder=" " className="peer h-14 px-4 pt-5 pb-2 bg-background/50 border-border/50 focus-visible:border-primary focus-visible:ring-0 rounded-sm transition-all placeholder:text-transparent" />
              </FormControl>
              <FormLabel className="absolute left-4 top-4 text-muted-foreground/70 text-sm transition-all duration-200 peer-focus:-top-2.5 peer-focus:left-2 peer-focus:text-xs peer-focus:text-primary peer-[:not(:placeholder-shown)]:-top-2.5 peer-[:not(:placeholder-shown)]:left-2 peer-[:not(:placeholder-shown)]:text-xs pointer-events-none bg-background/0 px-1">
                Phone Number
              </FormLabel>
              <FormMessage className="mt-1 text-xs" />
            </FormItem>} />

        {}
        <FormField control={control} name="inquiry_type" render={({
        field
      }) => <FormItem className="relative space-y-1">
              <FormLabel className="text-xs uppercase tracking-wider text-muted-foreground ml-1">
                Inquiry Type
              </FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger className="h-14 bg-background/50 border-border/50 focus:ring-0 focus:border-primary rounded-sm">
                    <SelectValue placeholder="Select an option" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="Estate Planning">Estate Planning</SelectItem>
                  <SelectItem value="Tax Strategy">Tax Strategy</SelectItem>
                  <SelectItem value="Life Insurance">Life Insurance</SelectItem>
                  <SelectItem value="Investment Management">Investment Management</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage className="text-xs" />
            </FormItem>} />

        {}
        <FormField control={control} name="assets_range" render={({
        field
      }) => <FormItem className="relative space-y-1">
              <FormLabel className="text-xs uppercase tracking-wider text-muted-foreground ml-1">
                Estimated Asset Range
              </FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger className="h-14 bg-background/50 border-border/50 focus:ring-0 focus:border-primary rounded-sm">
                    <SelectValue placeholder="Select an option" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="Less than $1M">Less than $1M</SelectItem>
                  <SelectItem value="$1M – $5M">$1M – $5M</SelectItem>
                  <SelectItem value="$5M – $10M">$5M – $10M</SelectItem>
                  <SelectItem value="$10M or more">$10M or more</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage className="text-xs" />
            </FormItem>} />

        {}
        <FormField control={control} name="message" render={({
        field
      }) => <FormItem className="relative space-y-0 md:col-span-2">
              <FormControl>
                <textarea {...field} placeholder=" " className="peer flex min-h-[160px] w-full rounded-sm border border-border/50 bg-background/50 px-4 pt-6 pb-2 text-sm shadow-sm placeholder:text-transparent focus-visible:outline-none focus-visible:border-primary focus-visible:ring-0 disabled:cursor-not-allowed disabled:opacity-50 resize-y transition-all" />
              </FormControl>
              <FormLabel className="absolute left-4 top-5 text-muted-foreground/70 text-sm transition-all duration-200 peer-focus:-top-2.5 peer-focus:left-2 peer-focus:text-xs peer-focus:text-primary peer-[:not(:placeholder-shown)]:-top-2.5 peer-[:not(:placeholder-shown)]:left-2 peer-[:not(:placeholder-shown)]:text-xs pointer-events-none bg-background/0 px-1">
                Message / Details
              </FormLabel>
              <FormMessage className="mt-1 text-xs" />
            </FormItem>} />
      </div>

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-sm">
          <p className="text-destructive text-sm text-center">{submitError}</p>
        </div>}

      <div className="pt-4">
        <Button type="submit" disabled={isSubmitting} size="lg" className="w-full md:w-auto min-w-[240px] h-14 text-base font-medium tracking-wide uppercase bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg shadow-primary/10">
          {isSubmitting ? <>
              <Loader className="mr-2 h-5 w-5 animate-spin" />
              Processing...
            </> : "Submit Request"}
        </Button>
      </div>
    </Form>;
}